import {
  IconDashboard,
  IconUsers,
  IconUserSearch,
  IconUserCheck,
  IconClipboardCheck,
  IconCash,
  IconFileDescription,
  IconClipboardList,
  IconPlayerPlay,
  IconWorldWww,
  IconCalendar,
  IconSettings,
  IconSearch,
  IconShieldLock,
  IconCalculator,
  IconCertificate2,
  IconReportMoney,
  type Icon,
} from "@tabler/icons-react"
import type { UserRole } from "@/store/roleSlice"
import type { FeatureName, Features } from "@/lib/features"
import {
  APPOINTMENTS_ACCESS_PERMISSIONS,
  APPOINTMENTS_ADMIN_PERMISSION,
  APPOINTMENTS_MANAGE_PERMISSION,
} from "@/lib/appointments-access"

export interface NavItem {
  title: string
  url: string
  icon: Icon
  /** Permission code required to see this nav item. If undefined, always visible. */
  requiredPermission?: string
  /**
   * Any one of these permissions is enough. For a page that serves more than one audience — the
   * appointments calendar shows a different view per permission — a single `requiredPermission`
   * would hide the link from half the people who can use it.
   */
  requiredAnyPermission?: string[]
  /** A feature flag that must be on as well, for a section whose API only exists behind the flag. */
  requiredFeature?: FeatureName
  /** Whether child items should be fetched dynamically. */
  dynamicChildren?: boolean
  /** Static child items. */
  children?: NavSubItem[]
}

export interface NavSubItem {
  title: string
  url: string
  /**
   * Permission code required to see this page, on top of whatever opens its section. A section can
   * serve people who hold different permissions — appointments is open to `appointments:view` — while
   * some of the pages inside it are administrators' work only.
   */
  requiredPermission?: string
  /**
   * Left out of the sidebar, so the page is reached only by its URL. It stays here so the header
   * still names it.
   */
  unlisted?: boolean
  /**
   * Left out of the sidebar while this feature flag is on: a page whose work has moved elsewhere
   * behind the flag, and whose route redirects there.
   */
  hiddenWhenFeature?: FeatureName
}

const allNavItems = {
  dashboard: {
    title: "Dashboard",
    url: "/dashboard",
    icon: IconDashboard,
    requiredPermission: "dashboard:view",
  },
  teamPerformance: {
    title: "Team Performance",
    url: "/team-performance",
    icon: IconUsers,
    requiredPermission: "team-performance:view",
  },
  fundingSources: {
    title: "Funding Sources",
    url: "/funding-sources",
    icon: IconCash,
    requiredPermission: "funding-sources:view",
  },
  benefitPrograms: {
    title: "Benefit Programs",
    url: "/benefit-programs",
    icon: IconFileDescription,
    requiredPermission: "benefit-programs:view",
    dynamicChildren: true,
  },
  benefitApplications: {
    title: "Benefit Applications",
    url: "/benefit-applications",
    icon: IconClipboardList,
    requiredPermission: "benefit-applications:view",
  },
  people: {
    title: "People",
    url: "/people",
    icon: IconUserSearch,
    requiredPermission: "people:view",
  },
  changeRequests: {
    title: "Change Requests",
    url: "/change-requests",
    icon: IconClipboardCheck,
    requiredPermission: "people:review-changes",
  },
  benefitRuns: {
    title: "Benefit Runs",
    url: "/benefit-runs",
    icon: IconPlayerPlay,
    requiredPermission: "benefit-runs:view",
  },
  pledgeReports: {
    title: "Pledge Reports",
    url: "/pledge-reports",
    icon: IconReportMoney,
    requiredPermission: "pledge-reports:view",
  },
  asciiExports: {
    title: "Certified ASCII",
    url: "/ascii-exports",
    icon: IconFileDescription,
    requiredPermission: "benefit-runs:execute",
  },
  pseOnline: {
    title: "PSE Online",
    url: "/pse-online",
    icon: IconWorldWww,
    requiredPermission: "pse-online:view",
  },
  appointments: {
    title: "Appointments",
    url: "/appointments",
    icon: IconCalendar,
    // Either permission opens the page — manage gets the office's day, view gets their own week —
    // so the link has to follow the same rule the page itself applies (see appointments-access).
    requiredAnyPermission: APPOINTMENTS_ACCESS_PERMISSIONS,
    requiredFeature: "amsBooking",
    children: [
      { title: "Incomplete Bookings", url: "/appointments/incomplete" },
      // Every site's appointments, for anybody in the section: view may read them all (Q7).
      { title: "Search", url: "/appointments/search" },
      // Both queues are the office's rather than one person's: deciding what becomes of a flagged
      // booking, and fixing whose roster an appointment sits on. Somebody who only takes
      // appointments has neither to do.
      { title: "Booking Review", url: "/appointments/review", requiredPermission: "appointments:manage" },
      { title: "Unassigned Hosts", url: "/appointments/unassigned-hosts", requiredPermission: "appointments:manage" },
      // CMS is the source of truth the calendar reads; this is what Cal knows that CMS was never told.
      // A diagnostic rather than a queue anyone works through, so it's opened by URL only, and only
      // by whoever holds the direct-grant appointments:admin.
      { title: "Data Gaps", url: "/appointments/data-gaps", requiredPermission: APPOINTMENTS_ADMIN_PERMISSION, unlisted: true },
      { title: "Cancellations", url: "/appointments/cancellations", requiredPermission: "appointments:manage" },
      // Retired behind AmsAvailabilityWeekView (AB#807): overrides are made on the Availability week view,
      // and the route redirects there. With the flag off the page is listed as it always was, to
      // appointments:manage only (AB#842).
      {
        title: "Schedule Overrides",
        url: "/appointments/schedule-overrides",
        requiredPermission: APPOINTMENTS_MANAGE_PERMISSION,
        hiddenWhenFeature: "amsAvailabilityWeekView",
      },
      // Each site's weekly slot grid and each host's availability (AB#781). Administering the schedule
      // is manage's, so view is not offered the page at all (AB#842); the route sends view home too.
      { title: "Availability", url: "/appointments/availability", requiredPermission: APPOINTMENTS_MANAGE_PERMISSION },
      // Moving a host from one site to another from a date (AB#714): both the preview and the move take
      // appointments:manage, so view is not offered a page it could only be refused on.
      { title: "Move Staff", url: "/appointments/staff-moves", requiredPermission: "appointments:manage" },
      // When the public website and phone line may book. Its own permission, granted to Scheduling
      // Manager: closing booking turns every applicant away at once.
      { title: "Booking Season", url: "/appointments/booking-season", requiredPermission: "appointments:manage-booking-season" },
      // Per funding program, whether the public may book it (AB#607). The season's permission: every
      // program off closes public booking as the season does.
      { title: "Funding Switches", url: "/appointments/funding-switches", requiredPermission: "appointments:manage-booking-season" },
    ],
  },
  adminUsers: {
    title: "User Access",
    url: "/admin/users",
    icon: IconShieldLock,
    requiredPermission: "admin:view-access",
  },
  adminReviewers: {
    title: "Change Reviewers",
    url: "/admin/reviewers",
    icon: IconUserCheck,
    requiredPermission: "people:manage-reviewers",
  },
  adminIntakeCatalog: {
    title: "Intake Catalog",
    url: "/admin/intake-catalog",
    icon: IconSettings,
    requiredPermission: "intake-catalog:manage",
  },
  recertification: {
    title: "Recertification",
    url: "/recertification",
    icon: IconCertificate2,
    requiredPermission: "recertification:view",
  },
  benefitCalculator: {
    title: "Benefit Calculator",
    url: "/benefit-calculator",
    icon: IconCalculator,
    requiredPermission: "benefit-calculator:use",
  },
} as const satisfies Record<string, NavItem>

/** All possible nav items in display order. */
const allNavItemsList: NavItem[] = [
  allNavItems.dashboard,
  allNavItems.teamPerformance,
  allNavItems.fundingSources,
  allNavItems.benefitPrograms,
  allNavItems.benefitApplications,
  allNavItems.people,
  allNavItems.changeRequests,
  allNavItems.benefitRuns,
  allNavItems.pledgeReports,
  allNavItems.asciiExports,
  allNavItems.pseOnline,
  allNavItems.appointments,
  allNavItems.adminUsers,
  allNavItems.adminReviewers,
  allNavItems.adminIntakeCatalog,
  allNavItems.recertification,
  allNavItems.benefitCalculator,
]

/**
 * Legacy role-based nav mapping. Used as fallback when permissions haven't loaded.
 */
const navByRole: Record<UserRole, NavItem[]> = {
  admin: [
    allNavItems.dashboard,
    allNavItems.teamPerformance,
    allNavItems.fundingSources,
    allNavItems.benefitPrograms,
    allNavItems.benefitApplications,
    allNavItems.people,
    allNavItems.benefitRuns,
    allNavItems.pledgeReports,
    allNavItems.asciiExports,
    allNavItems.adminUsers,
    allNavItems.adminIntakeCatalog,
    allNavItems.recertification,
    allNavItems.benefitCalculator,
  ],
  data_staff: [
    allNavItems.dashboard,
    allNavItems.benefitApplications,
    allNavItems.people,
    allNavItems.benefitRuns,
    allNavItems.pledgeReports,
    allNavItems.asciiExports,
    allNavItems.pseOnline,
    allNavItems.recertification,
    allNavItems.benefitCalculator,
  ],
  proofer: [
    allNavItems.benefitApplications,
  ],
  intake_staff: [
    allNavItems.dashboard,
    allNavItems.benefitApplications,
    allNavItems.people,
    // No appointments: it is shown only on a permission (see appointments-access), never on a legacy role slug.
    allNavItems.benefitCalculator,
  ],
  guest: [
    allNavItems.dashboard,
  ],
}

// Shown to all roles
export const secondaryNav: NavItem[] = [
  {
    title: "Settings",
    url: "/settings",
    icon: IconSettings,
    requiredPermission: "settings:view",
  },
  {
    title: "Search",
    url: "/search",
    icon: IconSearch,
    requiredPermission: "search:use",
  },
]

/** Whether the user holds what an item asks for: the single code, and any one of the alternatives. */
function isVisible(item: Pick<NavItem, "requiredPermission" | "requiredAnyPermission">, permissions: string[]): boolean {
  if (item.requiredPermission && !permissions.includes(item.requiredPermission)) return false
  if (item.requiredAnyPermission && !item.requiredAnyPermission.some(code => permissions.includes(code))) return false
  return true
}

/** The item with only the child pages this user may open, less any a feature flag has retired. */
function withVisibleChildren(item: NavItem, permissions: string[], features: Features): NavItem {
  if (!item.children) return item
  return {
    ...item,
    children: item.children.filter(child =>
      !child.unlisted
      && !(child.hiddenWhenFeature && features[child.hiddenWhenFeature])
      && isVisible(child, permissions)),
  }
}

/**
 * Returns nav items filtered by the user's permissions and the feature flags.
 * Falls back to role-based nav if permissions are empty.
 */
export function getNavForRole(role: UserRole | null, permissions: string[] | undefined, features: Features): NavItem[] {
  const isFeatureOn = (item: NavItem) => !item.requiredFeature || features[item.requiredFeature]
  if (permissions && permissions.length > 0) {
    return allNavItemsList
      .filter((item) => isVisible(item, permissions) && isFeatureOn(item))
      .map(item => withVisibleChildren(item, permissions, features))
  }
  if (!role) return []
  // No permissions to check a child page against, so one that asks for a permission stays hidden
  // rather than being offered on the strength of a role.
  return navByRole[role].filter(isFeatureOn).map(item => withVisibleChildren(item, [], features))
}

/**
 * Returns secondary nav items filtered by the user's permissions.
 */
export function getSectionTitle(pathname: string): string {
  const items = [...allNavItemsList, ...secondaryNav].flatMap(item => [item, ...(item.children ?? [])])
  return items.filter(item => pathname === item.url || pathname.startsWith(`${item.url}/`))
    .sort((a, b) => b.url.length - a.url.length)[0]?.title ?? "MSC Energy"
}

export function getSecondaryNav(permissions?: string[]): NavItem[] {
  if (permissions && permissions.length > 0) {
    return secondaryNav.filter((item) => isVisible(item, permissions))
  }
  return secondaryNav
}
